import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import Icon from 'components/base/Icon';

import Account from 'layouts/shared/Account';
import AllowAccess from 'utils/allowAccess';
import Queries from 'graphql/Queries';

const enterNavWrapper = keyframes`
  0% {
    transform: translateY(-1.5rem);
  }
`;

const NavWrapper = styled.div`
  align-items: start;
  background: var(--purpleTT);
  display: flex;
  flex-direction: column;
  height: ${props => props.height};
  height: 100vh;
  justify-content: space-between;
  left: 0;
  padding: 4rem 1.5rem 1.5rem;
  position: fixed;
  top: 0;
  transform: translateX(-100%);
  transition: 400ms transform var(--quintOut);
  width: 80%;
  z-index: 3;
  ${props => props.active && css`
    transform: translateX(0);
  `}

  @media (min-width: 23em) {
    width: 19rem;
  }
  @media (min-width: 50em) {
    align-items: center;
    animation: ${enterNavWrapper} 600ms var(--quintOut);
    background: none;
    border-bottom: 2px solid var(--gray);
    display: flex;
    flex-direction: row;
    grid-column: 1 / span 1;
    height: initial;
    left: initial;
    padding: initial;
    position: initial;
    top: initial;
    transform: initial;
    width: initial;
    z-index: initial;
    ${props => props.active && css`
      display: block;
    `}
    ${props => props.location === 'mobile' && css`
      display: none;
    `}
  }
  @media (min-width: 79em) {
    grid-column: 1 / span 2;
  }
  @media (max-height: 32em) and (max-width: 50em) {
    padding-top: 2.25rem;
  }
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.625rem;
  @media (min-width: 50em) {
    display: block;
    margin-left: initial;
  }
`;

const activeClassName = 'nav-item-active';

const Link = styled(NavLink)`
  box-sizing: border-box;
  border: 0;
  color: var(--purpleS);
  display: flex;
  font-size: var(--step-down-1);
  margin: 0 0 2.5rem;
  padding: 0 0 0 0.5rem;
  text-decoration: none;
  transition: color 400ms var(--quintOut);
  ${props => props.disabled && css`
    opacity: 0.25;
    pointer-events: none;
  `}

  span {
    padding-top: 0.0625rem;
  }
  svg {
    height: 1.375rem;
    margin-right: 1rem;
    width: 1.375rem;
    &:nth-of-type(2) {
      display: none;
    }
  }
  
  ${Links}:hover & {
    color: var(--purpleS);
    &:hover {
      color: var(--purpleS);
      opacity: 0.75;
    }
  }

  &.${activeClassName} {
    border: 0;
    color: var(--purpleS);
    svg {
      &:first-of-type {
        display: none;
      }
      &:nth-of-type(2) {
        display: block;
      }
    }
  }

  @media (min-width: 50em) {
    border-bottom: 0.3125rem solid transparent;
    color: rgba(var(--purpleS-rgb), 0.5);
    display: inline-block;
    font-size: var(--step-down-1);
    margin: 0.3125rem 3rem -0.125rem 0;
    padding: 1.625rem 0 1.375rem 0;

    span {
      padding-top: initial;
    }

    ${Links}:hover & {
      color: rgba(var(--purpleS-rgb), 0.5);
      &:hover {
        opacity: 1;
      }
    }

    &.${activeClassName} {
      border-bottom: 0.3125rem solid var(--purple);
    }
  }
`;

const NavButtons = styled.div`
  display: flex;
  flex-direction: row;
`;

export const NavButton = styled(NavLink)`
  align-items: center;
  background: var(--white);
  border-radius: 50%;
  border: 1px solid var(--purple);
  color: var(--purple);
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 2.75rem;
  padding: 0;
  transition: background-color 400ms var(--quintOut);
  width: 2.75rem;
  margin-right: 0.75em;
  ${props => props.disabled && css`
    opacity: 0.25;
    pointer-events: none;
  `}

  &:last-of-type {
    margin-right: 0;
  }
  &:active {
    color: var(--purpleT);
  }
  &:hover {
    background: var(--purpleTT);
    color: var(--purple);
  }

  svg {
    height: 1.25rem;
    width: 1.25rem;
  }

  &.${activeClassName} {
    background-color: var(--purple);
    color: var(--white);
    transition: background-color 400ms var(--quintOut);
    &:hover {
      background-color: var(--purple);
      color: var(--white);
    }
  }
`;

const Overlay = styled.div`
  display: none;
  ${props => props.active && css`
    display: block;
    background-color: rgba(102, 102, 102, 0.45);
    content: '';
    height: 100vh;
    position: fixed;
    width: 100vw;
    z-index: 3;
    left: 0;
    top: 0;
  `}
  @media (min-width: 50em) {
    ${props => props.active && css`
      display: none;
    `}
  }
`;

const Navigation = ({ projectId, location }) => (
  <Queries.Project variables={{ id: projectId }} loadingState="blank">
    {(project) => (
      <NavWrapper role="navigation" aria-label="Primary" location={location}>
        <Links>
          <Link exact to={`/project/${projectId}`} activeClassName={activeClassName}>
            Dashboard
          </Link>
          <Link to={`/project/${projectId}/updates`} activeClassName={activeClassName}>
            Updates
          </Link>
          <Link to={`/project/${projectId}/documents`} activeClassName={activeClassName}>
            Documents
          </Link>
          {project.harvestId && project.showFinancials && (
            <Link to={`/project/${projectId}/budget`} activeClassName={activeClassName}>
              Budget
            </Link>
          )}
        </Links>
        <AllowAccess permission="Employee">
          <NavButtons>
            <NavButton
              activeClassName={activeClassName}
              aria-label="Invite"
              disabled={project.isArchived}
              to={`/project/${projectId}/invite`}
            >
              <Icon id="invite" title="Invite" />
            </NavButton>
            <NavButton
              activeClassName={activeClassName}
              aria-label="Settings"
              disabled={project.isArchived}
              to={`/project/${projectId}/settings`}
            >
              <Icon id="setting" title="Setting" />
            </NavButton>
          </NavButtons>
        </AllowAccess>
      </NavWrapper>
    )}
  </Queries.Project>
);

export const NavigationMobile = class extends React.Component {
  state = {
    windowHeight: '100vh'
  };

  constructor(props) {
    super(props);

    this._handleResize = this._handleResize.bind(this);
  }

  componentDidMount() {
    this._handleResize();
  }
  componentWillMount() {
    window.addEventListener('resize', this._handleResize);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this._handleResize);
  }
  _handleResize() {
    this.setState({ windowHeight: (window.innerHeight+'px') });
  }

  render() {
    const { projectId, active, _handleMenuClick } = this.props;

    return (
      <Queries.Project variables={{ id: projectId }}>
        {(project) => (
          <React.Fragment>
            <Overlay active={active} onClick={_handleMenuClick} />
            <NavWrapper role="navigation" aria-label="Primary" location='mobile' active={active} height={this.state.windowHeight}>
              <Links>
                <Link exact to={`/project/${projectId}`} activeClassName={activeClassName} onClick={_handleMenuClick}>
                  <Icon id="mobile-dashboard-stroke" title="Dashboard" />
                  <Icon id="mobile-dashboard-fill" title="Dashboard" />
                  <span>Dashboard</span>
                </Link>
                <Link to={`/project/${projectId}/updates`} activeClassName={activeClassName} onClick={_handleMenuClick}>
                  <Icon id="mobile-updates-stroke" title="Updates" />
                  <Icon id="mobile-updates-fill" title="Updates" />
                  <span>Updates</span>
                </Link>
                <Link to={`/project/${projectId}/documents`} activeClassName={activeClassName} onClick={_handleMenuClick}>
                  <Icon id="mobile-documents-stroke" title="Documents" />
                  <Icon id="mobile-documents-fill" title="Documents" />
                  <span>Documents</span>
                </Link>
                {project.harvestId && project.showFinancials && (
                  <Link to={`/project/${projectId}/budget`} activeClassName={activeClassName} onClick={_handleMenuClick}>
                    <Icon id="mobile-budget-stroke" title="Budget" />
                    <Icon id="mobile-budget-fill" title="Budget" />
                    <span>Budget</span>
                  </Link>
                )}
                <AllowAccess permission="Employee">
                  <Link
                    to={`/project/${projectId}/invite`}
                    activeClassName={activeClassName}
                    onClick={_handleMenuClick}
                    disabled={project.isArchived}
                  >
                    <Icon id="mobile-invite-stroke" title="Invite" />
                    <Icon id="mobile-invite-fill" title="Invite" />
                    <span>Invite</span>
                  </Link>
                  <Link
                    to={`/project/${projectId}/settings`}
                    activeClassName={activeClassName}
                    onClick={_handleMenuClick}
                    disabled={project.isArchived}
                  >
                    <Icon id="mobile-settings-stroke" title="Settings" />
                    <Icon id="mobile-settings-fill" title="Settings" />
                    <span>Settings</span>
                  </Link>
                </AllowAccess>
              </Links>
              <Account location='mobile' />
            </NavWrapper>
          </React.Fragment>
        )}
      </Queries.Project>
    );
  }
};

Navigation.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Navigation;



// WEBPACK FOOTER //
// ./src/layouts/ProjectLayout/Navigation.js